/**
 * The add-in's conflict lookup: after a launch comes back 409, WHICH run is
 * the substrate's concurrent-run guard blocking on?
 * (findBlockingInstanceForEmail, below). The "reopen the pane on a running
 * workflow" lookup that also lived here was never wired and was deleted
 * 2026-10-06; the file keeps its name so the drawer's imports and jest mocks
 * stay put.
 *
 * Matching anchors (instance metadata, written at launch admit and preserved
 * by emit_push's metadata MERGE), in preference order:
 *   - `metadata.internet_message_id`        — THE DECLARED ANCHOR. The
 *     per-email identity start_workflow stamps at admit from the declared
 *     `internet_message_id` input. Prefer it: it is the one value the guard,
 *     the extraction cache and this lookup all agree on.
 *   - `metadata.graph_message_id`           — LEGACY. The Office item id the
 *     drawer threads into inputs (merged into metadata by start_workflow).
 *   - `metadata.email.internet_message_id`  — LEGACY. The RFC 5322 Message-ID
 *     the drawer mirrors for the launch-feedback flywheel.
 *
 * The two legacy anchors STAY for the rollout window: instances started before
 * the declared field existed carry no `metadata.internet_message_id`, and
 * dropping them would strand a live run's pane. All are compared against every
 * id the current mail item exposes (internetMessageId / restId / emailId),
 * internetMessageId FIRST, since which one rode depends on the Outlook host.
 */
import { orchestratorApi } from "./orchestratorApi";
import type { ActiveWorkflow } from "../types/workflow";
import { LAUNCH_BLOCKING_STATUSES } from "../types/workflow";
import { logger } from "../obs/logger";
// `EmailIdSource` / `candidateIds` / `instanceEmailAnchors` / `matchRunningInstance`
// moved to `@product-app/common/workflows/launch/core/findBlockingInstance`
// (ora agent's f795fe6cf, plan glittery-stargazing-treehouse Phase 3
// follow-up) — copied verbatim from this file, so this is a straight
// import swap. `matchRunningInstance` + `EmailIdSource` are re-exported
// below: `findRunningInstanceForEmail.test.ts` imports `matchRunningInstance`
// from this path directly.
import {
  candidateIds,
  matchRunningInstance,
  type EmailIdSource,
} from "@product-app/common/workflows/launch/core/findBlockingInstance";

export { matchRunningInstance };
export type { EmailIdSource };

async function lookup(
  email: EmailIdSource | null | undefined,
  statuses: readonly string[],
  context: string,
): Promise<ActiveWorkflow | null> {
  if (!email) return null;
  if (candidateIds(email).length === 0) return null;
  try {
    const result = await orchestratorApi.listInstances({
      status_filter: [...statuses],
      limit: 50,
    });
    return matchRunningInstance(result.items ?? [], email, statuses);
  } catch (err) {
    logger.warn(`${context}: lookup failed`, {
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}

/**
 * Resolve WHICH instance the substrate's concurrent-run guard is blocking on,
 * after a launch has already come back `conflict`.
 *
 * Called only on the conflict path, never on the happy path — the guard's 409
 * is what decides that a second launch is blocked, and this lookup only puts a
 * name and a status on it so the confirm dialog can tell the operator what is
 * already in flight and the acknowledgement can be scoped to that instance.
 * Doing it this way (rather than pre-checking every launch) keeps the normal
 * launch at one round-trip and makes it impossible for the add-in and the
 * substrate to disagree about what counts as blocking.
 *
 * Best-effort: null means "couldn't identify it", and the caller falls back to
 * showing the raw conflict message rather than an un-actionable dialog.
 */
export async function findBlockingInstanceForEmail(
  email: EmailIdSource | null | undefined,
): Promise<ActiveWorkflow | null> {
  return lookup(
    email,
    LAUNCH_BLOCKING_STATUSES,
    "findBlockingInstanceForEmail: cannot identify the blocking run",
  );
}
